import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  Link,
  Skeleton,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { useLocalStorageValue } from "@react-hookz/web";
import { Link as RouterLink } from "react-router";
import { makeDefaultFilters } from "../../../utils/default-filters";
import { PAGE_SIZE, useRecentlyEdited } from "../hooks/use-recently-edited";
import { AssetRow } from "./recently-edited/asset-row";
import { CommunicationRow } from "./recently-edited/communication-row";
import { RecentlyEditedEmpty } from "./recently-edited/recently-edited-empty";
import { SurveyRow } from "./recently-edited/survey-row";

import type { RecentlyEditedItem } from "../hooks/use-recently-edited";
import type { FC } from "react";

const ItemRow: FC<{ item: RecentlyEditedItem }> = ({ item }) => {
  switch (item.entity_type) {
    case "communication":
      return <CommunicationRow communication={item} />;
    case "survey":
      return <SurveyRow survey={item} />;
    case "digital_asset":
      return <AssetRow asset={item} />;
    default:
      return null;
  }
};

/** The Communications, Surveys and Digital Assets most recently edited, to pick up where left. */
export const RecentlyEditedSection: FC = () => {
  // Remembered in this browser; react-hookz falls back on the default where storage is blocked.
  const { value: mine = true, set: setMine } = useLocalStorageValue<boolean>(
    "dashboard-recently-edited-mine",
    { defaultValue: true },
  );
  const { items, reloading, error, reload, hasMore, loadingMore, loadMoreError, loadMore } =
    useRecentlyEdited(mine);

  return (
    <Box component="section">
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Typography variant="h6" component="h2">
          Recently edited
        </Typography>
        <Box sx={{ display: "flex", gap: 3 }}>
          <Link
            component={RouterLink}
            to={`/calendars/schedule?${makeDefaultFilters().toString()}`}
            variant="subtitle2"
            underline="hover"
            data-analytics-id="dashboard-recently-edited-upcoming-sends"
          >
            Upcoming Sends
          </Link>
          <Link
            component={RouterLink}
            to="/moments/history"
            variant="subtitle2"
            underline="hover"
            data-analytics-id="dashboard-recently-edited-sent-communications"
          >
            Sent Communications
          </Link>
        </Box>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "flex-end", my: 1 }}>
        <FormControlLabel
          label="My Content only"
          labelPlacement="start"
          control={
            <Switch
              size="small"
              checked={mine}
              onChange={(event) => setMine(event.target.checked)}
              data-analytics-id="dashboard-recently-edited-mine"
            />
          }
        />
      </Box>

      {error ? (
        <Alert
          severity="error"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={reload}
              data-analytics-id="dashboard-recently-edited-retry"
            >
              Try again
            </Button>
          }
        >
          Recently edited content couldn't be loaded.
        </Alert>
      ) : items === null ? (
        <Stack spacing={1.5}>
          {/* As many as the first page, so the rows land where their placeholders were. */}
          {Array.from({ length: PAGE_SIZE }, (_, index) => (
            <Skeleton key={index} variant="rounded" height={88} />
          ))}
        </Stack>
      ) : (
        // Faded while a change of filter loads, over what was there, rather than placeholders.
        <Box
          aria-busy={reloading}
          sx={(theme) => ({
            opacity: reloading ? 0.6 : 1,
            transition: theme.transitions.create("opacity"),
          })}
        >
          {items.length === 0 ? (
            <RecentlyEditedEmpty />
          ) : (
            <>
              <Stack component="ul" spacing={1.5} sx={{ listStyle: "none", m: 0, p: 0 }}>
                {items.map((item) => (
                  <ItemRow key={item.id} item={item} />
                ))}
              </Stack>
              {(hasMore || loadMoreError) && (
                <Box sx={{ textAlign: "center", mt: 2 }}>
                  {loadMoreError && (
                    <Typography variant="body2" sx={{ color: "error.main", mb: 1 }}>
                      Couldn't load more.
                    </Typography>
                  )}
                  <Button
                    onClick={() => void loadMore()}
                    loading={loadingMore}
                    disabled={reloading}
                    data-analytics-id="dashboard-recently-edited-load-more"
                  >
                    Load more
                  </Button>
                </Box>
              )}
            </>
          )}
        </Box>
      )}
    </Box>
  );
};
